import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, MoreVertical } from "lucide-react";
import client, { unwrap, apiErrorMessage } from "@/api/client";
import { catalogueProductsQuery } from "@/lib/sessionQuery";
import PreviewStage, { GENERATION_STAGES } from "@/components/window/PreviewStage";
import DesignPanel from "@/components/window/DesignPanel";
import StepRail from "@/components/window/StepRail";
import PreviewHistory from "@/components/window/PreviewHistory";
import CostingCard from "@/components/window/CostingCard";
import PreviewLightbox from "@/components/window/PreviewLightbox";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const FLOW = ["Catalogue", "Product", "Configuration", "AI Preview", "Costing", "Cart"];

// Key-order-independent fingerprint of a design, to tell whether the form still matches what was saved.
const fingerprint = (design) => JSON.stringify(Object.keys(design).sort().map((k) => [k, design[k] ?? null]));

const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function WindowWorkspacePage() {
  const { windowId } = useParams();
  const [params] = useSearchParams();
  const projectId = params.get("projectId");
  const roomId = params.get("roomId");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const refs = { product: useRef(null), fabric: useRef(null), style: useRef(null) };
  const stageRef = useRef(null);
  const costingRef = useRef(null);

  const [selectedCatalogueId, setSelectedCatalogueId] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [configForm, setConfigForm] = useState({ configType: "CURTAIN", fabric: "", colour: "", pattern: "", pleat: "", panels: null, mounting: "", trackOrRod: "", quantity: 1 });
  const [productConfigId, setProductConfigId] = useState(null);
  const [savedFingerprint, setSavedFingerprint] = useState(null);
  const [sourceFileId, setSourceFileId] = useState(null);
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [viewingPreviewId, setViewingPreviewId] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [panelsEdited, setPanelsEdited] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [costingForm, setCostingForm] = useState({ fabricRatePerMeter: 500, accessoriesCount: 1 });
  const [costingCalc, setCostingCalc] = useState(null);

  const { data: windowData } = useQuery({ queryKey: ["window", windowId], queryFn: () => client.get(`/windows/${windowId}`).then(unwrap) });
  // Panels come from the tenant's curtain costing rule, so the pre-filled value is what costing would use.
  const { data: panelSuggestion } = useQuery({
    queryKey: ["suggested-panels", windowData?.width],
    queryFn: () => client.get(`/costing/suggested-panels?widthInches=${windowData.width}`).then(unwrap),
    enabled: Number(windowData?.width) > 0,
  });
  const suggestedPanels = panelSuggestion?.panels ?? null;
  const { data: room } = useQuery({ queryKey: ["room", roomId], queryFn: () => client.get(`/rooms/${roomId}`).then(unwrap), enabled: !!roomId });
  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => client.get(`/projects/${projectId}`).then(unwrap),
    enabled: !!projectId,
  });
  const { data: cart } = useQuery({
    queryKey: ["cart", projectId],
    queryFn: () => client.get(`/carts?projectId=${projectId}`).then(unwrap).catch(() => ({ cart: null, items: [] })),
    enabled: !!projectId,
  });
  const { data: catalogues = [] } = useQuery({ queryKey: ["catalogues"], queryFn: () => client.get("/catalogues").then(unwrap) });
  // The shop's own pending/rejected catalogues come back too (for review on CataloguesPage), but can't be used until approved.
  const approvedCatalogues = catalogues.filter((c) => c.status === "APPROVED");
  const unapprovedCount = catalogues.length - approvedCatalogues.length;
  const { data: products = [], isLoading: productsLoading } = useQuery(catalogueProductsQuery(selectedCatalogueId));
  const { data: previews = [], refetch: refetchPreviews, isSuccess: previewsLoaded } = useQuery({
    queryKey: ["ai-previews", windowId],
    queryFn: () => client.get(`/ai-previews?windowId=${windowId}`).then(unwrap),
  });
  const { data: savedConfigs = [], isSuccess: configsLoaded } = useQuery({
    queryKey: ["product-configurations", windowId],
    queryFn: () => client.get(`/product-configurations?windowId=${windowId}`).then(unwrap),
  });
  // shopReference of a restored design, until its catalogue's products load and it can be selected.
  const [restoringProductId, setRestoringProductId] = useState(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!panelsEdited && suggestedPanels) setConfigForm((f) => ({ ...f, panels: suggestedPanels }));
  }, [suggestedPanels, panelsEdited]);

  // Panels only mean something for curtains; blinds neither cost by them nor should the AI prompt mention them.
  // An empty box sends nothing, and costing then works panels out itself.
  const specsOf = (form) => {
    const { panels, ...rest } = form;
    return form.configType === "CURTAIN" && panels > 0 ? { ...rest, panels } : rest;
  };
  const designSpecs = () => specsOf(configForm);

  const visibleProducts = products.filter((p) => p.type === configForm.configType);
  const viewingPreview = previews.find((p) => p._id === viewingPreviewId) || previews.find((p) => p.isSelectedFinal) || previews[0] || null;
  const viewingIdx = viewingPreview ? previews.findIndex((p) => p._id === viewingPreview._id) : -1;
  const isGenerating = viewingPreview?.status === "QUEUED" || viewingPreview?.status === "PROCESSING";
  const anyGenerating = previews.some((p) => p.status === "QUEUED" || p.status === "PROCESSING");

  const currentFingerprint = selectedProduct
    ? fingerprint({ catalogueId: selectedCatalogueId, shopReference: selectedProduct.shopReference, ...designSpecs() })
    : null;
  const designDirty = !productConfigId || currentFingerprint !== savedFingerprint;

  // On a return visit, reopen the saved design: the one behind the final preview, else the newest.
  // Without it, Costing and Generate stay locked although the window was already designed.
  useEffect(() => {
    if (restoredRef.current || productConfigId || !configsLoaded || !previewsLoaded) return;
    restoredRef.current = true;
    if (!savedConfigs.length) return;
    const finalConfigId = previews.find((p) => p.isSelectedFinal)?.productConfigurationId;
    const config =
      savedConfigs.find((c) => c._id === finalConfigId) ||
      [...savedConfigs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    const restored = {
      ...configForm,
      configType: config.configType,
      fabric: config.fabric || "",
      colour: config.colour || "",
      pattern: config.pattern || "",
      pleat: config.pleat || "",
      panels: config.panels ?? configForm.panels,
      mounting: config.mounting || "",
      trackOrRod: config.trackOrRod || "",
      quantity: config.quantity || 1,
    };
    setProductConfigId(config._id);
    setConfigForm(restored);
    setSavedFingerprint(fingerprint({ catalogueId: config.catalogueId, shopReference: config.shopReference, ...specsOf(restored) }));
    // A saved panel count is a deliberate choice; don't let the width suggestion replace it.
    if (config.panels) setPanelsEdited(true);
    setSelectedCatalogueId(config.catalogueId);
    setRestoringProductId(config.shopReference);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configsLoaded, previewsLoaded, savedConfigs, previews, productConfigId]);

  useEffect(() => {
    if (!restoringProductId || !products.length) return;
    const product = products.find((p) => p.shopReference === restoringProductId);
    if (product) setSelectedProduct(product);
    setRestoringProductId(null);
  }, [products, restoringProductId]);

  // On a return visit, restore the customer's photo from the latest preview so it shows again
  // and Regenerate can reuse it without a fresh upload.
  useEffect(() => {
    if (sourceFileId) return;
    const latest = previews.find((p) => p.sourceFileId && p.sourceUrl);
    if (latest) {
      setSourceFileId(latest.sourceFileId);
      setSourcePreviewUrl(latest.sourceUrl);
    }
  }, [previews, sourceFileId]);

  useEffect(() => {
    if (!isGenerating) {
      setStageIdx(0);
      return;
    }
    const t = setInterval(() => setStageIdx((s) => (s + 1) % GENERATION_STAGES.length), 1300);
    return () => clearInterval(t);
  }, [isGenerating, viewingPreview?._id]);

  const uploadPhoto = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await client.post("/files/upload?category=WINDOW_PHOTO", formData, { headers: { "Content-Type": "multipart/form-data" } });
      return unwrap(res);
    },
    onSuccess: (data, file) => {
      setSourceFileId(data.id);
      setSourcePreviewUrl(URL.createObjectURL(file));
      setShowOriginal(true);
      toast.success("Photo uploaded");
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Upload failed")),
  });

  const createConfig = useMutation({
    mutationFn: (payload) => client.post("/product-configurations", payload),
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed to save design"),
  });

  // Saves the current form as a new configuration and remembers it as the saved design.
  const saveDesign = async () => {
    const payload = {
      windowId,
      catalogueId: selectedCatalogueId,
      // Not masterProductId: external users only get the shop's own reference.
      shopReference: selectedProduct.shopReference,
      ...designSpecs(),
    };
    const res = await createConfig.mutateAsync(payload);
    const id = res.data.data._id;
    const { windowId: _w, ...design } = payload;
    setProductConfigId(id);
    setSavedFingerprint(fingerprint(design));
    setCostingCalc(null);
    return id;
  };

  const pollPreview = (id) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      const res = await client.get(`/ai-previews/${id}`);
      const status = unwrap(res).status;
      if (status === "SUCCESS" || status === "FAILED" || attempts > 20) {
        clearInterval(interval);
        refetchPreviews();
        if (status === "SUCCESS") toast.success("AI preview ready");
        if (status === "FAILED") toast.error("AI preview generation failed");
      }
    }, 1500);
  };

  const generatePreview = useMutation({
    mutationFn: (payload) => client.post("/ai-previews/generate", payload),
    onSuccess: (res) => {
      setViewingPreviewId(res.data.data._id);
      setShowOriginal(false);
      // Fetch now so the queued preview is in the list: the progress view and history key off it.
      refetchPreviews();
      pollPreview(res.data.data._id);
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed to start preview"),
  });

  const handleSave = async () => {
    if (!selectedProduct) return toast.error("Pick a fabric first");
    try {
      await saveDesign();
      toast.success(sourceFileId ? "Design saved" : "Design saved. Upload the window photo to generate a preview.");
    } catch {
      /* toast shown by the mutation */
    }
  };

  // Saves first only when the form has changed, so regenerating an unchanged design reuses it.
  const handleGenerate = async () => {
    try {
      const configId = designDirty ? await saveDesign() : productConfigId;
      generatePreview.mutate({ windowId, sourceFileId, productConfigurationId: configId, productId: selectedProduct?.id, specs: designSpecs() });
      if (window.innerWidth < 1024) stageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      /* toast shown by the mutation */
    }
  };

  const selectProduct = (p) => {
    setSelectedProduct(p);
    setConfigForm((f) => ({ ...f, fabric: p.fabricType || "", colour: p.colour || "", pattern: p.pattern || "" }));
  };

  const tryAnotherFabric = () => {
    if (!visibleProducts.length) return;
    const idx = visibleProducts.findIndex((p) => p.id === selectedProduct?.id);
    const next = visibleProducts[(idx + 1) % visibleProducts.length];
    selectProduct(next);
    document.querySelector(`[data-testid="select-product-card-${next.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  const selectFinal = useMutation({
    mutationFn: (previewId) => client.patch(`/ai-previews/${previewId}/select-final`, { windowId }),
    onSuccess: () => {
      toast.success("Set as final design");
      refetchPreviews();
    },
  });

  const deletePreview = useMutation({
    mutationFn: (previewId) => client.delete(`/ai-previews/${previewId}`),
    onSuccess: (_res, previewId) => {
      if (viewingPreviewId === previewId) setViewingPreviewId(null);
      toast.success("Preview deleted");
      refetchPreviews();
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Failed to delete preview")),
  });

  const calculateCosting = useMutation({
    mutationFn: (payload) => client.post("/costing/calculate", payload),
    onSuccess: (res) => {
      setCostingCalc(res.data.data);
      toast.success("Costing calculated");
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed"),
  });

  // The cart lives on the project page.
  const cartPath = projectId ? `/projects/${projectId}#project-cart` : null;
  // The design the customer picked; else the one on screen. Not session state, so it survives a reload.
  const cartPreview =
    previews.find((p) => p.isSelectedFinal && p.status === "SUCCESS") ||
    (viewingPreview?.status === "SUCCESS" ? viewingPreview : null);
  const inCart = addedToCart || (cart?.items || []).some((it) => it.windowId === windowId);

  const addToCart = useMutation({
    mutationFn: (payload) => client.post("/carts/items", payload),
    onSuccess: () => {
      setAddedToCart(true);
      toast.success("Added to project cart", cartPath ? { action: { label: "View cart", onClick: () => navigate(cartPath) } } : undefined);
      queryClient.invalidateQueries({ queryKey: ["cart", projectId] });
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed"),
  });

  const busy = createConfig.isPending || generatePreview.isPending || anyGenerating;
  const canSelectFinal = viewingPreview?.status === "SUCCESS" && !viewingPreview.isSelectedFinal;

  const steps = [
    { key: "product", label: "Product", done: !!selectedProduct, onClick: () => scrollTo(refs.product) },
    { key: "configuration", label: "Configuration", done: !!selectedProduct && !designDirty, onClick: () => scrollTo(refs.style) },
    { key: "preview", label: "AI Preview", done: previews.some((p) => p.status === "SUCCESS"), onClick: () => scrollTo(stageRef) },
    { key: "costing", label: "Costing", done: !!costingCalc || inCart, onClick: () => scrollTo(costingRef) },
    { key: "cart", label: "Cart", done: inCart, onClick: () => (inCart && cartPath ? navigate(cartPath) : scrollTo(costingRef)) },
  ];

  return (
    <div data-testid="window-workspace-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="min-w-0">
          <Link
            to={projectId ? `/projects/${projectId}` : "/projects"}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors mb-2"
            data-testid="window-workspace-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {[project?.name, room?.name].filter(Boolean).join(" / ") || "Project"}
          </Link>
          <h1 className="font-serif text-3xl sm:text-[2.4rem] leading-tight text-foreground">Design this window</h1>
          <p className="text-sm text-muted-foreground mt-1.5">{FLOW.join(" → ")}</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {canSelectFinal && (
            <Button
              variant="outline"
              data-testid="select-final-preview-button"
              disabled={selectFinal.isPending}
              onClick={() => selectFinal.mutate(viewingPreview._id)}
              className="bg-card border-success/50 text-success hover:bg-success/5 hover:text-success"
            >
              <Check className="h-4 w-4 mr-1.5" /> Select Design
            </Button>
          )}
          <Button
            variant="outline"
            data-testid="save-configuration-button"
            disabled={!selectedProduct || busy || !designDirty}
            onClick={handleSave}
            className="bg-card"
          >
            {createConfig.isPending ? "Saving…" : designDirty || !selectedProduct ? "Save Design" : (
              <><Check className="h-4 w-4 mr-1.5 text-success" /> Saved</>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More actions" data-testid="window-more-actions" className="bg-card">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem disabled={viewingPreview?.status !== "SUCCESS"} onSelect={() => setFullscreenOpen(true)}>View full screen</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => scrollTo(costingRef)}>Go to costing</DropdownMenuItem>
              {cartPath && <DropdownMenuItem onSelect={() => navigate(cartPath)}>View project cart</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!viewingPreview || isGenerating}
                className="text-danger focus:text-danger"
                onSelect={() => setPendingDeleteId(viewingPreview._id)}
              >
                Delete this preview
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Workspace: picture, design steps, progress rail */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_9.5rem] gap-5 items-start">
        <div ref={stageRef} className="scroll-mt-24">
          <PreviewStage
            preview={viewingPreview}
            previewIndex={viewingIdx}
            previewCount={previews.length}
            onStep={(dir) => {
              const next = previews[viewingIdx + dir];
              if (next) setViewingPreviewId(next._id);
            }}
            showOriginal={showOriginal && !!sourcePreviewUrl}
            setShowOriginal={setShowOriginal}
            sourceUrl={sourcePreviewUrl}
            isGenerating={isGenerating}
            stageIdx={stageIdx}
            onFullscreen={() => setFullscreenOpen(true)}
            onRetry={() => client.post(`/ai-previews/${viewingPreview._id}/retry`).then(() => refetchPreviews())}
            onUpload={(e) => e.target.files[0] && uploadPhoto.mutate(e.target.files[0])}
            uploading={uploadPhoto.isPending}
            window={windowData}
            roomName={room?.name}
          />
        </div>

        <DesignPanel
          refs={refs}
          configForm={configForm}
          setConfigForm={setConfigForm}
          onTypeChange={(type) => {
            setConfigForm((f) => ({ ...f, configType: type }));
            setSelectedProduct(null);
          }}
          catalogues={approvedCatalogues}
          unapprovedCount={unapprovedCount}
          catalogueId={selectedCatalogueId}
          setCatalogueId={setSelectedCatalogueId}
          products={visibleProducts}
          productsLoading={productsLoading}
          selectedProduct={selectedProduct}
          onSelectProduct={selectProduct}
          suggestedPanels={suggestedPanels}
          onPanelsChange={(panels) => {
            setPanelsEdited(true);
            setConfigForm((f) => ({ ...f, panels }));
          }}
          onResetPanels={() => {
            setPanelsEdited(false);
            setConfigForm((f) => ({ ...f, panels: suggestedPanels }));
          }}
          windowWidth={windowData?.width}
          onGenerate={handleGenerate}
          generateLabel={anyGenerating ? "Generating…" : previews.length ? "Regenerate with AI" : "Generate with AI"}
          generateDisabled={!selectedProduct || !sourceFileId || busy}
          generateHint={
            !sourceFileId
              ? "Upload the customer's window photo to generate a preview."
              : !selectedProduct
                ? "Pick a fabric to generate a preview."
                : null
          }
          onTryAnother={tryAnotherFabric}
          canTryAnother={visibleProducts.length > 1}
        />

        <div className="hidden xl:block pt-4 xl:sticky xl:top-24">
          <StepRail steps={steps} />
        </div>
      </div>

      <PreviewHistory
        previews={previews}
        viewingId={viewingPreview?._id}
        onView={(id) => {
          setViewingPreviewId(id);
          setShowOriginal(false);
          // Finished previews open in the gallery; failed or running ones show their state on the stage.
          const p = previews.find((x) => x._id === id);
          if (p?.status === "SUCCESS" && p.previewUrl) setFullscreenOpen(true);
        }}
        onDelete={setPendingDeleteId}
      />

      <CostingCard
        ref={costingRef}
        ready={!!productConfigId}
        stale={designDirty}
        costingForm={costingForm}
        setCostingForm={setCostingForm}
        calculating={calculateCosting.isPending}
        onCalculate={() =>
          calculateCosting.mutate({
            configType: configForm.configType,
            widthInches: windowData?.width,
            heightInches: windowData?.height,
            quantity: configForm.quantity,
            panels: designSpecs().panels,
            fabricRatePerMeter: costingForm.fabricRatePerMeter,
            accessoriesCount: costingForm.accessoriesCount,
            productConfigurationId: productConfigId,
          })
        }
        costing={costingCalc}
        adding={addToCart.isPending}
        onAddToCart={() =>
          addToCart.mutate({
            projectId,
            roomId,
            windowId,
            productConfigurationId: productConfigId,
            aiPreviewId: cartPreview?._id || null,
            costingCalculationId: costingCalc._id,
            quantity: configForm.quantity,
            price: costingCalc.finalPrice,
          })
        }
        inCart={inCart}
        cartPath={cartPath}
      />

      <PreviewLightbox
        open={fullscreenOpen}
        setOpen={setFullscreenOpen}
        previews={previews}
        currentId={viewingPreview?._id}
        onChange={(id) => {
          setViewingPreviewId(id);
          setShowOriginal(false);
        }}
      />

      <AlertDialog open={!!pendingDeleteId} onOpenChange={(open) => !open && setPendingDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this preview?</AlertDialogTitle>
            <AlertDialogDescription>It will be removed from this window's preview history. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction className="bg-danger hover:bg-danger/90" data-testid="confirm-delete-preview-button" onClick={() => deletePreview.mutate(pendingDeleteId)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
